import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

export const formatFullName = (
  name:
    | {
        firstName?: string;
        lastName?: string;
        middleName?: string | null;
      }
    | undefined,
) => {
  if (!name || !name.firstName || !name.lastName) return "";
  const { firstName, middleName, lastName } = name;

  const middleNamePart = isTruthy(middleName) ? `${middleName} ` : "";

  return `${firstName} ${middleNamePart}${lastName}`;
};

export const getFileExtension = (fileName: string): string | null => {
  const dotIndex = fileName.lastIndexOf(".");

  if (dotIndex === -1) {
    return null; // No extension found
  }

  return fileName.substring(dotIndex + 1).toLowerCase();
};

const constantToTitleCase = (str: string): string =>
  str
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

/**
 * Converts a camelCase string to a capitalized string with spaces.
 *
 * @param str - The camelCase string to be converted.
 * @returns The converted string with each word capitalized and separated by spaces.
 *
 * @example
 * ```typescript
 * const result = camelCaseToCapitalize("camelCaseString");
 * console.log(result); // "Camel Case String"
 * ```
 */
export const camelCaseToCapitalize = (str: string): string =>
  str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const snakeCaseToHuman = (str?: string | null): string => {
  if (isFalsy(str)) {
    return "";
  }

  const titleCase = constantToTitleCase(str);

  return titleCase.charAt(0) + titleCase.slice(1).toLowerCase();
};
/**
 * Converts a given string to title case.
 *
 * Replaces all underscores with spaces and
 * capitalizes the first letter of each word.
 *
 * @example
 * convertToTitleCase("hello_world") // "Hello World"
 *
 * @param {string} str String to convert to title case
 * @returns {string} String in title case
 */
export const convertToTitleCase = (str?: string) => {
  if (isFalsy(str)) {
    return "";
  }

  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
};

export const formatBooleanValue = (
  value: boolean | null | undefined,
): "No" | "Yes" => (isTruthy(value) ? "Yes" : "No");

export const WITH_DOTS = " • ";
/**
 * Joins an array of strings with a dot and a space.
 *
 * @example
 * joinWithDots(["Hello", "World"]) // "Hello • World"
 *
 * @param {string[]} array Array of strings to join
 * @returns {string} Joined string
 */
export const joinWithDots = (array: string[]) =>
  array.filter(isTruthy).join(WITH_DOTS);

export const useFormatStrings = () => {
  // eslint-disable-next-line unicorn/consistent-function-scoping
  function numberToWords(num: number) {
    const words = [
      m.number_zero(),
      m.number_one(),
      m.number_two(),
      m.number_three(),
      m.number_four(),
      m.number_five(),
      m.number_six(),
      m.number_seven(),
      m.number_eight(),
      m.number_nine(),
      m.number_ten(),
      m.number_eleven(),
      m.number_twelve(),
      m.number_thirteen(),
      m.number_fourteen(),
      m.number_fifteen(),
      m.number_sixteen(),
      m.number_seventeen(),
      m.number_eighteen(),
      m.number_nineteen(),
      m.number_twenty(),
    ];

    return words[num] || num; // Return the word if it's in the array, otherwise return the number itself
  }

  return {
    numberToWords,
  };
};
